import { useState } from "react";
import { toast } from "sonner";
import PolicyPage from "./PolicyPage";
import { Mail, MessageCircle, MapPin, Clock } from "lucide-react";
import { sendContactMessage } from "../lib/api";

// Layout-only scaffolding. Update copy / form submission target when ready.
const CHANNELS = [
  {
    icon: Mail,
    label: "Customer Care",
    value: "info@smibhaluxecandles.com",
    href: "mailto:info@smibhaluxecandles.com",
    note: "Orders, shipping, returns — replies within 1 business day.",
  },
  {
    icon: MessageCircle,
    label: "Wholesale & Press",
    value: "info@smibhaluxecandles.com",
    href: "mailto:info@smibhaluxecandles.com",
    note: "Partnerships, collaborations, and editorial enquiries.",
  },
  {
    icon: MapPin,
    label: "Studio + Warehouse",
    value: "21 Whittaker Cres, Cambridge, ON N1T 1Z4",
    note: "Visits by appointment only.",
  },
  {
    icon: Clock,
    label: "Care Hours",
    value: "Mon–Fri · 9:00 AM – 6:00 PM ET",
    note: "We close on Canadian statutory holidays.",
  },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await sendContactMessage(form);
      setSent(true);
      toast.success("Message received", { description: res?.message || "We'll be in touch soon." });
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Please try again or email info@smibhaluxecandles.com directly.";
      toast.error("We couldn't send your message", { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PolicyPage
      eyebrow="Get in Touch"
      title="Contact Smibha Luxe Candles"
      testid="page-contact"
    >
      <p>
        We love hearing from the people who light our candles. Reach the
        team through any of the channels below, or send us a note using the form.
      </p>

      <div className="grid sm:grid-cols-2 gap-0 border border-zinc-200 mt-10" data-testid="contact-channels">
        {CHANNELS.map((c, idx) => (
          <div
            key={c.label}
            className={`p-6 ${idx < CHANNELS.length - 1 ? "border-b sm:border-b-0" : ""} ${
              idx % 2 === 0 ? "sm:border-r" : ""
            } ${idx < 2 ? "sm:border-b" : ""} border-zinc-200`}
            data-testid={`contact-channel-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-semibold text-zinc-500 mb-3">
              <c.icon size={14} strokeWidth={2} />
              {c.label}
            </div>
            {c.href ? (
              <a href={c.href} className="text-base font-bold tracking-tight hover:text-[#002FA7]" style={{ fontFamily: "Outfit" }}>
                {c.value}
              </a>
            ) : (
              <div className="text-base font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>
                {c.value}
              </div>
            )}
            <p className="text-xs text-zinc-500 mt-2 leading-relaxed">{c.note}</p>
          </div>
        ))}
      </div>

      <h2 className="text-2xl font-black tracking-tight mt-16" style={{ fontFamily: "Outfit" }}>
        Send us a note
      </h2>
      <p>
        Use the form below and your message will land in our care team&rsquo;s inbox within
        seconds. We reply within one business day.
      </p>

      {sent ? (
        <div
          className="mt-6 border border-emerald-300 bg-emerald-50 p-6 text-emerald-900"
          data-testid="contact-success"
        >
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold mb-2">Thank you</div>
          <div className="font-bold tracking-tight text-lg" style={{ fontFamily: "Outfit" }}>
            Your message is on its way.
          </div>
          <p className="text-sm mt-2 leading-relaxed">
            We typically reply within one business day. In the meantime, you can also reach us at{" "}
            <a className="underline" href="mailto:info@smibhaluxecandles.com">info@smibhaluxecandles.com</a>.
          </p>
          <button
            onClick={() => setSent(false)}
            className="mt-4 text-[11px] uppercase tracking-[0.25em] font-bold underline"
            data-testid="contact-send-another"
          >
            Send another message
          </button>
        </div>
      ) : (
        <form
          className="mt-6 space-y-4"
          onSubmit={handleSubmit}
          data-testid="contact-form"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.3em] font-semibold text-zinc-500 block mb-2">
                Full name
              </span>
              <input
                type="text"
                required
                value={form.name}
                onChange={update("name")}
                className="w-full border border-zinc-300 px-3 h-11 text-sm focus:outline-none focus:border-zinc-900"
                data-testid="contact-name"
              />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.3em] font-semibold text-zinc-500 block mb-2">
                Email
              </span>
              <input
                type="email"
                required
                value={form.email}
                onChange={update("email")}
                className="w-full border border-zinc-300 px-3 h-11 text-sm focus:outline-none focus:border-zinc-900"
                data-testid="contact-email"
              />
            </label>
          </div>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.3em] font-semibold text-zinc-500 block mb-2">
              Subject
            </span>
            <input
              type="text"
              value={form.subject}
              onChange={update("subject")}
              className="w-full border border-zinc-300 px-3 h-11 text-sm focus:outline-none focus:border-zinc-900"
              data-testid="contact-subject"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.3em] font-semibold text-zinc-500 block mb-2">
              Message
            </span>
            <textarea
              rows={6}
              required
              minLength={10}
              value={form.message}
              onChange={update("message")}
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-900 resize-y"
              data-testid="contact-message"
            />
          </label>
          <button
            type="submit"
            disabled={submitting}
            className="bg-zinc-900 text-white px-6 py-3 text-xs uppercase tracking-[0.25em] font-bold hover:bg-black transition disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="contact-submit"
          >
            {submitting ? "Sending..." : "Send message"}
          </button>
        </form>
      )}

      <p className="text-sm text-zinc-500 mt-10">
        Smibha Luxe Candles · 21 Whittaker Cres, Cambridge, ON N1T 1Z4, Canada
      </p>
    </PolicyPage>
  );
}
